import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, FormikHelpers } from 'formik';
import {
  isInvitationAccepted,
  isInvitationDeclined,
  isInvitationHasBeenSent,
  isInvitationSendPending,
  isParentsEmailSet,
  isTooSoonToEnableResend,
} from './UtilityFunctions';
import ParentInviteButton from './ParentInviteButton/ParentInviteButton';
import InvitationControl from './InvitationControl/InvitationControl';
import InvitationControlPrefix from './InvitationControlPrefix/InvitationControlPrefix';
import ParentStatus from './ParentStatus/ParentStatus';
import EmptyClassMessage from './EmptyClassMessage/EmptyClassMessage';
import styles from '../SCSS/Students.module.scss';
import { StudentProxyType } from '@peekapak/types/DataProtocolTypes';

interface Props {
  isSendInviteError: boolean;
  isTemporarilyAllowResend: boolean;
  onInvite: (student: StudentProxyType) => Promise<void>;
  onResetError: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSetParentsEmail: (student: StudentProxyType, email: string) => Promise<StudentProxyType | null>;
  sendInviteErrorSid: string | undefined;
  sendParentInvitationSignal: (isSending: boolean) => void;
  sendPendingList: string[];
  studentList: StudentProxyType[];
}

interface FormValues {
  parentEmail: string;
}

const ConnectParentInvitationList = ({
  isSendInviteError,
  isTemporarilyAllowResend,
  onInvite,
  onResetError,
  onSetParentsEmail,
  sendInviteErrorSid,
  sendParentInvitationSignal,
  sendPendingList,
  studentList,
}: Props) => {
  return (
    <div className={styles.InvitationListContainer}>
      {studentList && studentList.length > 0 ? (
        studentList.map((student: StudentProxyType, index: number) => {
          const initialFormValues: FormValues = {
            parentEmail:
              student.listOfParentProxies && student.listOfParentProxies[0] && student.listOfParentProxies[0].email
                ? student.listOfParentProxies[0].email
                : '',
          };

          const computeFlags = () => {
            let isInviteSuppression = false;
            let isEnableResend = false;
            let isInviteSendPending = false;
            const isAccepted = isInvitationAccepted(student);
            const isDeclined = isInvitationDeclined(student);

            if (isParentsEmailSet(student)) {
              if (!isAccepted && !(isDeclined && !isTemporarilyAllowResend) && isInvitationHasBeenSent(student)) {
                if (isTooSoonToEnableResend(student)) {
                  isInviteSuppression = true;
                } else {
                  isEnableResend = true;
                }
              }

              if (
                !isAccepted &&
                !(isDeclined && !isTemporarilyAllowResend) &&
                isInvitationSendPending(student, sendPendingList)
              ) {
                isInviteSendPending = true;
              }
            }

            return {
              isInviteSuppression,
              isEnableResend,
              isInviteSendPending,
              isAccepted,
              isDeclined,
            };
          };

          const { isInviteSuppression, isEnableResend, isAccepted, isDeclined } = computeFlags();

          const firstElementId = index === 0 ? 'firstInvitationListItem' : undefined;

          const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
            sendParentInvitationSignal(true);
            try {
              setSubmitting(true);
              const result = await onSetParentsEmail(student, values.parentEmail);
              if (result) {
                await onInvite(result);
              }
              sendParentInvitationSignal(false);
              setSubmitting(false);
            } catch (error) {
              console.error(error);
              sendParentInvitationSignal(false);
            } finally {
              setSubmitting(false);
              sendParentInvitationSignal(false);
            }
          };

          // TODO: InvitationControl's onSetParentsEmail doesn't seem to do anything once the email
          // change returns... doesn't seem right
          return (
            <div id={firstElementId} key={index} className={styles['flex-row']}>
              <Formik initialValues={initialFormValues} onSubmit={handleSubmit}>
                {() => {
                  return (
                    <Form className={styles.connectParentInvitationForm}>
                      <div className={styles.invitationControlPrefix}>
                        <InvitationControlPrefix student={student} />
                      </div>
                      <div className={styles.ConnectParentInvitationListItemOuter}>
                        <div
                          className={`${styles.InvitationListItem} ${styles.ConnectParentInvitationListItem} ${styles['flex-row']}`}
                        >
                          <InvitationControl
                            onEmailChange={(value) => onSetParentsEmail(student, value)}
                            isInviteSuppression={isInviteSuppression}
                          />
                          <ParentInviteButton
                            isEnableResend={isEnableResend}
                            isInviteAccepted={isAccepted}
                            isInviteDeclined={isDeclined}
                            isSendInviteError={isSendInviteError}
                            isInviteSuppression={isInviteSuppression}
                            onResetError={onResetError}
                            sendInviteErrorSid={sendInviteErrorSid}
                            student={student}
                          />
                        </div>
                        <div className={styles.parentStatusText}>
                          <ParentStatus parentsList={student.listOfParentProxies} student={student} statusOnly />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          );
        })
      ) : (
        <EmptyClassMessage />
      )}
    </div>
  );
};

ConnectParentInvitationList.propTypes = {
  studentList: PropTypes.arrayOf(PropTypes.object),
};

export default ConnectParentInvitationList;
